import { CallOptions } from '@connectrpc/connect';
import {
  BehaviorSubject,
  defer,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  repeat,
  retry,
  share,
  tap,
  timeout,
  timer,
} from 'rxjs';
import { fromServerStream, GrpcStreamResponse, messagesOnly } from './grpc-rx';

export type GrpcStreamStatus = 'idle' | 'connecting' | 'established' | 'error';

export interface GrpcStreamConnectionState {
  connected: boolean;
  connectionError: boolean;
}

// The streams service pings every subscriber once a minute, so two missed pings mean a dead stream.
const STALE_STREAM_TIMEOUT_MS = 150_000;
const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export function streamConnectionState(status: GrpcStreamStatus): GrpcStreamConnectionState {
  return { connected: status === 'established', connectionError: status === 'error' };
}

export abstract class BaseGrpcService<T, M> {
  private readonly statusSubject = new BehaviorSubject<GrpcStreamStatus>('idle');

  readonly status$: Observable<GrpcStreamStatus> = this.statusSubject.pipe(distinctUntilChanged());

  readonly items$: Observable<T> = defer(() => fromServerStream(signal => this.openStream({ signal }))).pipe(
    tap({ subscribe: () => this.setStatus('connecting'), next: () => this.setStatus('established') }),
    timeout({ each: STALE_STREAM_TIMEOUT_MS }),
    messagesOnly<M>(),
    map(message => this.transformFn(message)),
    retry({
      resetOnSuccess: true,
      delay: (error, attempt) => {
        console.warn('gRPC stream dropped, reconnecting', error);
        this.setStatus('error');
        return timer(Math.min(RECONNECT_DELAY_MS * attempt, MAX_RECONNECT_DELAY_MS));
      },
    }),
    repeat({
      delay: () => {
        this.setStatus('connecting');
        return timer(RECONNECT_DELAY_MS);
      },
    }),
    finalize(() => this.setStatus('idle')),
    share({ resetOnRefCountZero: true }),
  );

  protected abstract readonly transformFn: (message: M) => T;

  protected abstract openStream(options: CallOptions): AsyncIterable<GrpcStreamResponse<M>>;

  private setStatus(status: GrpcStreamStatus): void {
    this.statusSubject.next(status);
  }
}
